import { debugLog, WAIT_GUARANTEE_THRESHOLD, MATCHMAKING_CONFIG } from './matchmakingConfig.js';
import { computeWaitGuaranteeThreshold, shouldExpandPoolBeyondMinTier } from './sessionDebt.js';
import { isScoringFirstMatchmakingEnabled } from './scoringFirstMatchmaking.js';
import { getSinglesPairingPolicy, MATCH_FORMAT_SINGLES } from './competitiveMatchFormat.js';
import { pairAllowedByPolicy } from './singlesCourtEngine.js';
import { countEffectiveMales, countWomensEligibleFemales, countPhysicalFemales, countPhysicalMales, isMensDoublesEligible } from './competitiveGender.js';
import {
  buildUnitsFromPlayers,
  expandUnitsToPlayers,
  getUnitSize,
  getUnitPlayers,
  filterUnits,
  unitContainsPlayer,
  getTotalPlayerCount,
  createPartnershipUnit,
  createScheduledUnit,
  UNIT_TYPES,
} from './unitBuilder.js';

function isInPartnership(playerId, partnerships = []) {
  if (!partnerships || partnerships.length === 0) return false;
  return partnerships.some(p => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    return p1 === playerId || p2 === playerId;
  });
}

function getPartnerId(playerId, partnerships = []) {
  if (!partnerships || partnerships.length === 0) return null;
  for (const p of partnerships) {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    if (p1 === playerId) return p2;
    if (p2 === playerId) return p1;
  }
  return null;
}

/**
 * Filter out players whose partners are not in the eligible pool.
 * Partners must ALWAYS play together - if one partner is eligible but the other isn't,
 * neither should be in the pool.
 */
/**
 * Check if a unit passes cooldown filter
 * Unit fails if ANY player in the unit fails cooldown
 * 
 * ✨ NORMALIZED STATE: Read isOnCooldown directly from database
 * No more calculation = no more race conditions!
 */
function unitPassesCooldown(unit, playersById, playerWaitMetrics) {
  const unitPlayers = getUnitPlayers(unit);
  return unitPlayers.every(playerId => {
    const waitMetrics = playerWaitMetrics[playerId];
    // Read directly from database - authoritative source of truth
    const isOnCooldown = waitMetrics?.isOnCooldown ?? false;
    // Invert: if ON cooldown, they FAIL the filter (return false)
    return !isOnCooldown;
  });
}

/**
 * Check if a pool can satisfy game constraints (validation gate)
 * Fast feasibility check using heuristics and sampling
 * 
 * @param {Object[]} units - Array of units to validate
 * @param {Object} playersById - Map of player objects
 * @param {Array} partnerships - Array of partnerships
 * @param {string} gameTypeRestriction - Game type restriction
 * @param {number} [playersPerMatch=4] - 4 doubles, 2 singles
 * @param {Object} [playerWaitMetrics={}] - Wait metrics (includes mensDoublesEligible flags)
 * @returns {boolean} True if pool can form at least one valid game
 */
function canPoolSatisfyConstraints(units, playersById, partnerships, gameTypeRestriction, playersPerMatch = 4, playerWaitMetrics = {}) {
  const playerIds = expandUnitsToPlayers(units);
  const ppm = playersPerMatch;

  if (playerIds.length < ppm) {
    return false;
  }

  if (ppm === 4) {
    // HEURISTIC: If we only have exactly 4 players, the pool lacks flexibility
    // for handling interaction conflicts. Require at least 5 players for safety.
    if (playerIds.length === 4) {
      debugLog('⚠️ Validation gate: Only 4 players - insufficient flexibility for interactions');
      return false;
    }
  }
  // Singles: allow exactly 2 players in pool (one valid match is enough)

  // Check 1: Game type feasibility (instant check)
  if (gameTypeRestriction && gameTypeRestriction !== 'all_types') {
    const genders = playerIds.map(id => playersById[id]?.gender);
    const males = genders.filter(g => g === 'male').length;
    const females = genders.filter(g => g === 'female').length;

    if (ppm === 2) {
      const policy = getSinglesPairingPolicy(gameTypeRestriction, MATCH_FORMAT_SINGLES);
      if (gameTypeRestriction === 'mens_only' && males < 2) {
        debugLog('⚠️ Validation gate (singles): Cannot satisfy mens_only', { males });
        return false;
      }
      if (gameTypeRestriction === 'ladies_only' && females < 2) {
        debugLog('⚠️ Validation gate (singles): Cannot satisfy ladies_only', { females });
        return false;
      }
      if (gameTypeRestriction === 'levels_only' && males < 2 && females < 2) {
        debugLog('⚠️ Validation gate (singles): Cannot satisfy levels_only', { males, females });
        return false;
      }
      if (gameTypeRestriction === 'mixed_only') {
        // Enum for doubles; singles policy treats as all-types — need any two players
        if (playerIds.length < 2) return false;
      } else if (gameTypeRestriction === 'balanced_only' || gameTypeRestriction === 'balanced_and_mens_vs_mixed') {
        const canSameGender = males >= 2 || females >= 2;
        const canCross = policy.allowUnbalancedSingles && males >= 1 && females >= 1;
        if (!canSameGender && !canCross) {
          debugLog('⚠️ Validation gate (singles): Cannot satisfy balanced constraint', {
            males,
            females,
            allowUnbalancedSingles: policy.allowUnbalancedSingles,
          });
          return false;
        }
      }
    } else {
      if (gameTypeRestriction === 'balanced_only') {
        const canFormMixed = males >= 2 && females >= 2;
        // Effective males include physical males + mens-doubles-eligible females
        const effectiveMales = countEffectiveMales(playerIds, playersById, playerWaitMetrics);
        const canFormAllMale = effectiveMales >= 4;
        // Womens pool excludes eligible females
        const womensEligible = countWomensEligibleFemales(playerIds, playersById, playerWaitMetrics);
        const canFormAllFemale = womensEligible >= 4;

        if (!canFormMixed && !canFormAllMale && !canFormAllFemale) {
          debugLog('⚠️ Validation gate: Cannot satisfy balanced_only constraint', {
            males, females, effectiveMales, womensEligible, need: '2+2 or 4+0(eff) or 0+4(womens)'
          });
          return false;
        }
      } else if (gameTypeRestriction === 'mixed_only') {
        if (males < 2 || females < 2) {
          debugLog('⚠️ Validation gate: Cannot satisfy mixed_only constraint', {
            males, females, need: '2+2'
          });
          return false;
        }
      } else if (gameTypeRestriction === 'mens_only') {
        if (males < 4) {
          debugLog('⚠️ Validation gate: Cannot satisfy mens_only constraint', {
            males, need: '4'
          });
          return false;
        }
      } else if (gameTypeRestriction === 'ladies_only') {
        if (females < 4) {
          debugLog('⚠️ Validation gate: Cannot satisfy ladies_only constraint', {
            females, need: '4'
          });
          return false;
        }
      }
    }
  }
  
  // Check 2: Partnership completeness (instant check)
  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    partnerships.forEach(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    });
    
    // Check if all partnerships have both partners in pool
    for (const playerId of playerIds) {
      const partnerId = partnershipMap.get(playerId);
      if (partnerId && !playerIds.includes(partnerId)) {
        debugLog('⚠️ Validation gate: Partnership incomplete', {
          player: playersById[playerId]?.name,
          partner: playersById[partnerId]?.name,
          partnerInPool: false
        });
        return false;
      }
    }
  }
  
  // Check 3: Sample-based validation (quick check, not exhaustive)
  const sampleSize = Math.min(10, playerIds.length);
  let validSampleFound = false;

  if (ppm === 2) {
    for (let i = 0; i < sampleSize; i++) {
      const shuffled = [...playerIds].sort(() => Math.random() - 0.5);
      const sample = shuffled.slice(0, 2);
      if (sample.length !== 2) continue;
      const team1 = [playersById[sample[0]]].filter(Boolean);
      const team2 = [playersById[sample[1]]].filter(Boolean);
      if (team1.length !== 1 || team2.length !== 1) continue;
      if (isValidGameType(team1, team2, gameTypeRestriction, ppm, playerWaitMetrics)) {
        validSampleFound = true;
        break;
      }
    }
  } else {
    for (let i = 0; i < sampleSize; i++) {
      const shuffled = [...playerIds].sort(() => Math.random() - 0.5);
      const sample = shuffled.slice(0, 4);

      if (sample.length !== 4) continue;

      const team1 = [sample[0], sample[1]].map(id => playersById[id]);
      const team2 = [sample[2], sample[3]].map(id => playersById[id]);

      if (isValidGameType(team1, team2, gameTypeRestriction, ppm, playerWaitMetrics)) {
        validSampleFound = true;
        break;
      }
    }
  }
  
  if (!validSampleFound) {
    debugLog('⚠️ Validation gate: No valid combination found in sample', {
      samplesChecked: sampleSize,
      poolSize: playerIds.length
    });
    return false;
  }
  
  return true;
}

/**
 * Check if team split satisfies game type restriction (simplified, for validation only).
 * For balanced_only, mens-doubles-eligible females count as effective males so that
 * [eligible-F + M] vs [M + M] passes as a balanced mens-doubles game.
 *
 * @param {Array}  team1
 * @param {Array}  team2
 * @param {string} gameTypeRestriction
 * @param {number} [playersPerMatch=4]
 * @param {Object} [playerWaitMetrics={}]
 */
function isValidGameType(team1, team2, gameTypeRestriction, playersPerMatch = 4, playerWaitMetrics = {}) {
  if (!gameTypeRestriction || gameTypeRestriction === 'all_types') return true;

  if (playersPerMatch === 2 && team1.length === 1 && team2.length === 1) {
    const policy = getSinglesPairingPolicy(gameTypeRestriction, MATCH_FORMAT_SINGLES);
    return pairAllowedByPolicy(team1[0]?.gender, team2[0]?.gender, policy);
  }

  // Physical counts for mixed detection (eligibility never changes mixed formation)
  const t1PhysMales = team1.filter(p => (p?.gender || '').toLowerCase() === 'male').length;
  const t1PhysFemales = team1.filter(p => (p?.gender || '').toLowerCase() === 'female').length;
  const t2PhysMales = team2.filter(p => (p?.gender || '').toLowerCase() === 'male').length;
  const t2PhysFemales = team2.filter(p => (p?.gender || '').toLowerCase() === 'female').length;

  const t1Mixed = t1PhysMales > 0 && t1PhysFemales > 0;
  const t2Mixed = t2PhysMales > 0 && t2PhysFemales > 0;

  if (gameTypeRestriction === 'balanced_only') {
    // Eligible females count as effective males for mens-doubles compositions.
    // Non-eligible females on a mens-side team → invalid.
    const t1IdsFromObjs = team1.map(p => p?.id).filter(Boolean);
    const t2IdsFromObjs = team2.map(p => p?.id).filter(Boolean);

    const t1EffMales = countEffectiveMales(t1IdsFromObjs, Object.fromEntries(team1.map(p => [p?.id, p])), playerWaitMetrics);
    const t2EffMales = countEffectiveMales(t2IdsFromObjs, Object.fromEntries(team2.map(p => [p?.id, p])), playerWaitMetrics);
    const t1EffAllMale = t1EffMales === team1.length;
    const t2EffAllMale = t2EffMales === team2.length;

    const t1WomensElig = countWomensEligibleFemales(t1IdsFromObjs, Object.fromEntries(team1.map(p => [p?.id, p])), playerWaitMetrics);
    const t2WomensElig = countWomensEligibleFemales(t2IdsFromObjs, Object.fromEntries(team2.map(p => [p?.id, p])), playerWaitMetrics);
    const t1EffAllFemale = t1WomensElig === team1.length && t1PhysFemales === team1.length;
    const t2EffAllFemale = t2WomensElig === team2.length && t2PhysFemales === team2.length;

    // Balanced: both effectively-all-male, both effectively-all-female, or both mixed
    if (t1EffAllMale && t2EffAllMale) return true;
    if (t1EffAllFemale && t2EffAllFemale) return true;
    if (t1Mixed && t2Mixed) return true;
    return false;
  } else if (gameTypeRestriction === 'mixed_only') {
    return t1Mixed && t2Mixed;
  } else if (gameTypeRestriction === 'mens_only') {
    return t1PhysMales === 2 && t2PhysMales === 2;
  } else if (gameTypeRestriction === 'ladies_only') {
    return t1PhysFemales === 2 && t2PhysFemales === 2;
  }

  return true;
}

/**
 * Get effective games played for a player.
 */
function getPlayerEffectiveGames(playerId, playersById) {
  const player = playersById[playerId];
  if (!player) return 0;
  return player.effectiveGamesPlayed ?? player.gamesPlayedThisSession ?? 0;
}

/**
 * Average games for a unit (single or partnership). Partnerships are treated as one fairness unit.
 */
function getUnitAverageGames(unit, playersById) {
  const unitPlayers = getUnitPlayers(unit);
  if (unitPlayers.length === 0) return 0;
  const total = unitPlayers.reduce(
    (sum, playerId) => sum + getPlayerEffectiveGames(playerId, playersById),
    0,
  );
  return total / unitPlayers.length;
}

/**
 * Get effective games for a unit (average — used for threshold / guarantee comparisons).
 */
function getUnitEffectiveGames(unit, playersById) {
  return getUnitAverageGames(unit, playersById);
}

function isUnitAtSessionMin(unit, playersById, sessionMinGames) {
  if (unit.type === UNIT_TYPES.SCHEDULED) return false;
  if (sessionMinGames === null || sessionMinGames === undefined) return false;
  return Math.abs(getUnitAverageGames(unit, playersById) - sessionMinGames) < 1e-6;
}

function filterUnitsAtSessionMin(units, playersById, sessionMinGames) {
  return units.filter((unit) => isUnitAtSessionMin(unit, playersById, sessionMinGames));
}

/**
 * Feasibility-only pool expansion for scoring-first (no structural guarantees).
 */
function finalizeScoringFirstPool({
  safetyNetFilteredUnits,
  sessionMinGames,
  playersById,
  ppm,
  cooldownRelaxed,
  playersPassedCooldown,
  relaxationDetails,
}) {
  const uniqueGamesPlayed = new Set();
  safetyNetFilteredUnits.forEach((unit) => {
    if (unit.type === UNIT_TYPES.SCHEDULED) return;
    uniqueGamesPlayed.add(getUnitAverageGames(unit, playersById));
  });

  const sortedUniqueGames = [...uniqueGamesPlayed].sort((a, b) => a - b);
  const minGamesIndex = sortedUniqueGames.indexOf(sessionMinGames);
  const startIndex = minGamesIndex >= 0 ? minGamesIndex : 0;
  const minPlayersNeeded = ppm === 2 ? 3 : 5;
  let eligiblePoolUnits = [];
  let thresholdIndex = startIndex;
  const maxIndex = Math.max(0, sortedUniqueGames.length - 1);

  while (thresholdIndex <= maxIndex) {
    const threshold = sortedUniqueGames[thresholdIndex];
    eligiblePoolUnits = safetyNetFilteredUnits.filter(
      (unit) => unit.type !== UNIT_TYPES.SCHEDULED
        && getUnitAverageGames(unit, playersById) <= threshold,
    );
    const playerCount = getTotalPlayerCount(eligiblePoolUnits);
    if (playerCount >= minPlayersNeeded || thresholdIndex >= maxIndex) {
      break;
    }
    thresholdIndex += 1;
  }

  if (getTotalPlayerCount(eligiblePoolUnits) === 0) {
    eligiblePoolUnits = safetyNetFilteredUnits.filter(
      (unit) => unit.type !== UNIT_TYPES.SCHEDULED,
    );
  }

  const finalThreshold = sortedUniqueGames[thresholdIndex] ?? sessionMinGames;
  const details = [...relaxationDetails];
  if (thresholdIndex > startIndex) {
    details.push(`Scoring-first: expanded pool to ${finalThreshold} games for feasibility`);
  }
  details.push('Scoring-first: eligibility-only pool (no structural guarantees)');

  return {
    pool: eligiblePoolUnits,
    guaranteedPlayers: [],
    flexiblePlayers: eligiblePoolUnits,
    minGames: sessionMinGames,
    expandedThreshold: finalThreshold,
    thresholdsUsed: thresholdIndex - startIndex + 1,
    cooldownRelaxed,
    playersPassedCooldown,
    relaxationDetails: details,
    guaranteedByWait: [],
    scoringFirstMode: true,
  };
}

/**
 * Session minimum games: one value per fairness unit (singles + partnership averages).
 * Scheduled-game players are excluded from the min calculation.
 */
function calculateSessionMinGames(players, playersById, partnerships = [], scheduledGames = []) {
  const eligiblePlayers = players.filter((id) => !isInScheduledGame(id, scheduledGames));
  if (eligiblePlayers.length === 0) {
    const allGames = players.map((id) => getPlayerEffectiveGames(id, playersById));
    return allGames.length > 0 ? Math.min(...allGames) : 0;
  }

  const units = buildUnitsFromPlayers(eligiblePlayers, partnerships);
  if (units.length === 0) return 0;

  return Math.min(...units.map((unit) => getUnitAverageGames(unit, playersById)));
}

/**
 * Check if a unit is eligible for guarantee based on min games
 * Unit is eligible when its average games are at or below session min.
 */
function isUnitEligibleForGuarantee(unit, playersById, minGames) {
  if (minGames === null || minGames === undefined) return false;
  return getUnitAverageGames(unit, playersById) <= minGames;
}

/**
 * Check if a unit can play based on min games
 * Unit can play when average games are below min+1 (same tier as solo players).
 */
function canUnitPlay(unit, playersById, minGames) {
  if (minGames === null || minGames === undefined) return true;
  return getUnitAverageGames(unit, playersById) < minGames + 1;
}

function isInScheduledGame(playerId, scheduledGames = []) {
  // Ensure scheduledGames is an array before calling .some()
  if (!scheduledGames || !Array.isArray(scheduledGames) || scheduledGames.length === 0) {
    return false;
  }
  return scheduledGames.some(game => {
    return game.team1_player1_id === playerId ||
      game.team1_player2_id === playerId ||
      game.team2_player1_id === playerId ||
      game.team2_player2_id === playerId;
  });
}

function calculateMinGamesExcludingPartnerships(players, playersById, partnerships = [], scheduledGames = []) {
  return calculateSessionMinGames(players, playersById, partnerships, scheduledGames);
}

function wouldReachMinPlus2(playerId, playersById, minGames) {
  if (minGames === null || minGames === undefined) return false;
  const player = playersById[playerId];
  if (!player) return false;
  const effectiveGames = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
  return effectiveGames >= minGames + 1;
}

function wouldBeFairAfterGame(team1, team2, playersById, currentMinGames) {
  const allPlayerIds = [...team1, ...team2];
  const proposedPlayersGames = {};
  allPlayerIds.forEach(playerId => {
    const player = playersById[playerId];
    if (!player) return;
    const current = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
    proposedPlayersGames[playerId] = current + 1;
  });
  const proposedPlayerGamesAfter = Object.values(proposedPlayersGames);
  if (proposedPlayerGamesAfter.length === 0) {
    return true;
  }
  const newMinGamesAfterGame = Math.min(...proposedPlayerGamesAfter);
  return proposedPlayerGamesAfter.every(games => games <= newMinGamesAfterGame + 1);
}

function isPartnershipEligibleForGuarantee(player1Id, player2Id, playersById, minGames) {
  if (minGames === null || minGames === undefined) return false;
  const player1 = playersById[player1Id];
  const player2 = playersById[player2Id];
  if (!player1 || !player2) return false;
  const p1Games = getPlayerEffectiveGames(player1Id, playersById);
  const p2Games = getPlayerEffectiveGames(player2Id, playersById);
  return (p1Games + p2Games) / 2 <= minGames;
}

function canPartnershipPlay(player1Id, player2Id, playersById, minGames) {
  if (minGames === null || minGames === undefined) return true;
  const player1 = playersById[player1Id];
  const player2 = playersById[player2Id];
  if (!player1 || !player2) return false;
  const p1Games = getPlayerEffectiveGames(player1Id, playersById);
  const p2Games = getPlayerEffectiveGames(player2Id, playersById);
  return (p1Games + p2Games) / 2 < minGames + 1;
}

function isScheduledGameEligibleForGuarantee(scheduledGame, playersById, minGames) {
  if (minGames === null || minGames === undefined) return false;
  const players = [
    scheduledGame.team1_player1_id,
    scheduledGame.team1_player2_id,
    scheduledGame.team2_player1_id,
    scheduledGame.team2_player2_id,
  ].filter(Boolean);
  if (players.length !== 4) return false;
  return players.every(playerId => {
    const player = playersById[playerId];
    if (!player) return false;
    const games = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
    return games <= minGames;
  });
}

function canScheduledGamePlay(scheduledGame, playersById, minGames) {
  if (minGames === null || minGames === undefined) return true;
  const players = [
    scheduledGame.team1_player1_id,
    scheduledGame.team1_player2_id,
    scheduledGame.team2_player1_id,
    scheduledGame.team2_player2_id,
  ].filter(Boolean);
  if (players.length !== 4) return false;
  return players.every(playerId => {
    const player = playersById[playerId];
    if (!player) return false;
    const games = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
    return games < minGames + 1;
  });
}

/**
 * @typedef {Object} PoolBuildResult
 * @property {Object[]} pool - Array of units (partnership units, single units, scheduled units)
 * @property {Object[]} guaranteedPlayers - Array of guaranteed units
 * @property {Object[]} flexiblePlayers - Array of flexible units
 * @property {number} minGames
 * @property {number} expandedThreshold
 * @property {number} thresholdsUsed
 * @property {boolean} cooldownRelaxed
 * @property {Set<string>} playersPassedCooldown
 * @property {string[]} relaxationDetails
 * @property {Object[]} guaranteedByWait - Units guaranteed due to high courtsObserved count
 */

/**
 * @param {number} [waitGuaranteeThreshold] - Optional. When provided (e.g. session's number of courts),
 *   players with courtsObserved >= this value get wait guarantee. When omitted, uses WAIT_GUARANTEE_THRESHOLD from config.
 * @param {number} [playersPerMatch=4] - Players per game (4 doubles, 2 singles)
 * @param {boolean} [matchPreferencesEnabled=true] - When false, hard-preference pool exclusion is skipped
 * @param {boolean} [cooldownEnabled=false] - When false, cooldown unit filter and auto-relax are skipped
 * @param {Object} [sessionState=null] - Session state for session-debt pool expansion policy
 */
function buildEligiblePool(remainingPlayers, playersById, partnerships = [], scheduledGames = [], minGames, playerWaitMetrics = {}, forceRelaxCooldown = false, gameTypeRestriction = 'all_types', waitGuaranteeThreshold = undefined, playersPerMatch = 4, matchPreferencesEnabled = true, cooldownEnabled = false, sessionState = null) {
  const ppm = Math.max(2, playersPerMatch);
  const effectiveWaitGuarantee = computeWaitGuaranteeThreshold(waitGuaranteeThreshold ?? WAIT_GUARANTEE_THRESHOLD);
  debugLog('🏊 buildEligiblePool called with:', {
    remainingPlayersCount: remainingPlayers.length,
    playerIds: remainingPlayers,
    gameTypeRestriction,
    waitGuaranteeThreshold: effectiveWaitGuarantee,
    playersPerMatch: ppm,
  });

  // STEP 0: Filter players - EXCLUDE any player whose partner is not in the remainingPlayers pool
  // Partners must BOTH be available to play - if one is missing, exclude both
  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    partnerships.forEach(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    });
  }

  const remainingPlayersSet = new Set(remainingPlayers);
  const playersWithAvailablePartners = remainingPlayers.filter(playerId => {
    const partnerId = partnershipMap.get(playerId);
    if (!partnerId) {
      // No partnership - player can play
      return true;
    }
    // Has a partner - check if partner is also in remainingPlayers
    const partnerIsAvailable = remainingPlayersSet.has(partnerId);
    if (!partnerIsAvailable) {
      const playerName = playersById[playerId]?.name || playerId;
      const partnerName = playersById[partnerId]?.name || partnerId;
      debugLog(`⏸️ [POOL BUILDER] Excluding ${playerName} from pool - partner ${partnerName} is not available. Will wait until partner is available.`);
      return false;
    }
    // Both partners are available - include player
    return true;
  });

  // STEP 0.5: Exclude hard-preference players whose preferred game type cannot be formed
  // from the current pool. They voluntarily sit out this rotation.
  // Feasibility rules (doubles, 4-player game):
  //   prefer_mixed   → pool needs ≥ 2 men AND ≥ 2 women
  //   prefer_levels (male)   → pool needs ≥ 4 men
  //   prefer_levels (female) → pool needs ≥ 4 women
  // Hard strength also blocks violating combinations at scoring time (never relaxed).
  let playersAfterHardPref = playersWithAvailablePartners;

  if (matchPreferencesEnabled) {
  const poolMaleCount = playersWithAvailablePartners.filter(
    id => playersById[id]?.gender?.toLowerCase() === 'male'
  ).length;
  const poolFemaleCount = playersWithAvailablePartners.filter(
    id => playersById[id]?.gender?.toLowerCase() === 'female'
  ).length;
  const poolEffectiveMaleCount = countEffectiveMales(
    playersWithAvailablePartners,
    playersById,
    playerWaitMetrics,
  );

  const hardPrefExcluded = new Set();
  playersWithAvailablePartners.forEach(playerId => {
    const metrics = playerWaitMetrics[playerId];
    if (!metrics) return;
    const { matchPreference, preferenceStrength } = metrics;
    if (preferenceStrength !== 'hard' || !matchPreference || matchPreference === 'no_preference') return;

    const gender = playersById[playerId]?.gender?.toLowerCase();
    let canFormPreferredGame = true;

    if (matchPreference === 'prefer_mixed') {
      if (isMensDoublesEligible(playerId, playerWaitMetrics)) {
        canFormPreferredGame =
          (poolMaleCount >= 2 && poolFemaleCount >= 2)
          || countPhysicalMales(playersWithAvailablePartners, playersById) >= 3;
      } else {
        canFormPreferredGame = poolMaleCount >= 2 && poolFemaleCount >= 2;
      }
    } else if (matchPreference === 'prefer_levels') {
      if (gender === 'male') {
        canFormPreferredGame = poolEffectiveMaleCount >= 4;
      } else if (gender === 'female') {
        if (isMensDoublesEligible(playerId, playerWaitMetrics)) {
          canFormPreferredGame =
            poolEffectiveMaleCount >= 4
            && countPhysicalMales(playersWithAvailablePartners, playersById) >= 3;
        } else {
          canFormPreferredGame = poolFemaleCount >= 4;
        }
      }
      // Unknown gender — don't exclude
    }

    if (!canFormPreferredGame) {
      hardPrefExcluded.add(playerId);
      debugLog(`🚫 [HARD PREF] Holding back ${playersById[playerId]?.name || playerId} — preferred game type (${matchPreference}) not achievable with current pool (M:${poolMaleCount} F:${poolFemaleCount})`);
    }
  });

  playersAfterHardPref = hardPrefExcluded.size > 0
    ? playersWithAvailablePartners.filter(id => !hardPrefExcluded.has(id))
    : playersWithAvailablePartners;

  if (hardPrefExcluded.size > 0) {
    debugLog(`🎯 [HARD PREF] Excluded ${hardPrefExcluded.size} player(s) with hard preference this rotation`);
  }
  }

  // Now filter partnerships to only include those where BOTH partners are in the filtered pool
  const filteredPlayersSet = new Set(playersAfterHardPref);
  const completePartnerships = partnerships.filter(p => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    return p1 && p2 && filteredPlayersSet.has(p1) && filteredPlayersSet.has(p2);
  });

  // STEP 1: Convert players to units (partnerships become units, singles stay as single units)
  // This ensures partnerships are always together from the start
  const allUnits = buildUnitsFromPlayers(playersAfterHardPref, completePartnerships);
  debugLog(`🔧 Converted ${playersAfterHardPref.length} players to ${allUnits.length} units (${remainingPlayers.length - playersAfterHardPref.length} excluded due to missing partners or hard preference)`);

  // STEP 2: Track which players passed cooldown (for scoring bonuses)
  // ✨ NORMALIZED STATE: Read isOnCooldown directly from database
  const playersWhoPassedCooldown = playersAfterHardPref.filter(playerId => {
    const waitMetrics = playerWaitMetrics[playerId];
    // Read directly from database - authoritative source of truth
    const isOnCooldown = waitMetrics?.isOnCooldown ?? false;
    // If NOT on cooldown, they passed the filter
    return !isOnCooldown;
  });

  // STEP 3: Filter units by cooldown
  // Unit fails cooldown if ANY player in the unit fails cooldown
  const unitsPassedCooldown = allUnits.filter(unit => {
    const passes = unitPassesCooldown(unit, playersById, playerWaitMetrics);
    if (!passes) {
      const unitPlayers = getUnitPlayers(unit);
      const playerNames = unitPlayers.map(id => playersById[id]?.name || id).join(', ');
      debugLog(`🚫 Unit ${unit.id} (${playerNames}) filtered by cooldown`);
    }
    return passes;
  });

  // Check if cooldown is disabled via session setting or simulation override
  const cooldownDisabled = cooldownEnabled === false
    || MATCHMAKING_CONFIG.simulationOverrides?.enableCooldown === false;
  
  // When forceRelaxCooldown is true OR cooldown is disabled, we treat cooldown as a soft preference
  const cooldownFilteredUnits = (forceRelaxCooldown || cooldownDisabled) ? allUnits : unitsPassedCooldown;

  debugLog(`🏊 After cooldown filter: ${cooldownFilteredUnits.length} units (from ${allUnits.length} total units)`);

  const playersPassedCooldown = new Set(playersWhoPassedCooldown);

  // STEP 4: Calculate min games (need to expand units to players for this calculation)
  const cooldownFilteredPlayers = expandUnitsToPlayers(cooldownFilteredUnits);
  let sessionMinGames = calculateSessionMinGames(
    cooldownFilteredPlayers,
    playersById,
    partnerships,
    scheduledGames,
  );

  let safetyNetFilteredUnits = cooldownFilteredUnits;
  let cooldownRelaxed = false;
  let relaxationDetails = [];

  // STEP 5: Units at session min (singles + partnerships using unit average games)
  let unitsAtMin = filterUnitsAtSessionMin(
    safetyNetFilteredUnits,
    playersById,
    sessionMinGames,
  );

  // Count guaranteed units (partnerships and scheduled games)
  let tempGuaranteedUnitCount = 0;
  const guaranteedPartnershipUnits = [];
  safetyNetFilteredUnits.forEach(unit => {
    if (unit.type === UNIT_TYPES.PARTNERSHIP) {
      if (isUnitEligibleForGuarantee(unit, playersById, sessionMinGames)) {
        guaranteedPartnershipUnits.push(unit);
        tempGuaranteedUnitCount += 1; // 1 unit = 2 players
      }
    }
  });

  const guaranteedScheduledUnits = [];
  // NOTE: Scheduled games are NOT added to the pool
  // They are handled separately in Dashboard.js and promoted to next-on-court when ready

  // Calculate total player count at min (units at min + guaranteed units)
  const playersAtMinCount = getTotalPlayerCount(unitsAtMin);
  const totalAtMinWithCooldown = playersAtMinCount + (tempGuaranteedUnitCount * 2); // Approximate (partnerships=2, scheduled=4, but we count units)

  // STEP 6: Check if we need to relax cooldown
  const totalPlayerCount = getTotalPlayerCount(safetyNetFilteredUnits);
  if (!forceRelaxCooldown && !cooldownDisabled) {
    // Normal path: cooldown can be relaxed automatically when there are not
    // enough players (or not enough at minimum) to form a fair pool.
    if (totalPlayerCount < ppm || totalAtMinWithCooldown < ppm) {
      debugLog('🔄 Relaxing cooldown filter - not enough players with cooldown');
      cooldownRelaxed = true;
      relaxationDetails.push(`Cooldown relaxed to reach ${ppm} eligible players`);
      safetyNetFilteredUnits = allUnits; // Use all units (no cooldown filter)
      const allPlayers = expandUnitsToPlayers(allUnits);
      sessionMinGames = calculateSessionMinGames(
        allPlayers,
        playersById,
        partnerships,
        scheduledGames,
      );
      unitsAtMin = filterUnitsAtSessionMin(
        safetyNetFilteredUnits,
        playersById,
        sessionMinGames,
      );
    }
  } else {
    // Explicit forced relaxation: treat cooldown as already relaxed.
    cooldownRelaxed = true;
    relaxationDetails.push('Cooldown fully relaxed due to lack of valid combinations');
    safetyNetFilteredUnits = allUnits;
    const allPlayers = expandUnitsToPlayers(allUnits);
    sessionMinGames = calculateSessionMinGames(
      allPlayers,
      playersById,
      partnerships,
      scheduledGames,
    );
    unitsAtMin = filterUnitsAtSessionMin(
      safetyNetFilteredUnits,
      playersById,
      sessionMinGames,
    );
  }

  debugLog(`🏊 After safety net filter: ${safetyNetFilteredUnits.length} units (${getTotalPlayerCount(safetyNetFilteredUnits)} players, cooldown relaxed: ${cooldownRelaxed})`);

  // NOTE: No need for filterIncompletePartnerships - partnerships are already units!

  debugLog('🔍 buildEligiblePool debug:', {
    remainingPlayersCount: remainingPlayers.length,
    playersWithAvailablePartners: playersWithAvailablePartners.length,
    excludedDueToMissingPartner: remainingPlayers.length - playersWithAvailablePartners.length,
    totalUnits: allUnits.length,
    cooldownFilteredUnits: cooldownFilteredUnits.length,
    safetyNetFilteredUnits: safetyNetFilteredUnits.length,
    safetyNetFilteredPlayers: getTotalPlayerCount(safetyNetFilteredUnits),
    sessionMinGames,
    unitsAtMinCount: unitsAtMin.length,
    playersAtMinCount: getTotalPlayerCount(unitsAtMin),
    cooldownRelaxed,
    playersPassedCooldownCount: playersPassedCooldown.size,
  });

  if (isScoringFirstMatchmakingEnabled()) {
    return finalizeScoringFirstPool({
      safetyNetFilteredUnits,
      sessionMinGames,
      playersById,
      ppm,
      cooldownRelaxed,
      playersPassedCooldown,
      relaxationDetails,
    });
  }

  // Rebuild guaranteed partnership units (already done above, but ensure they're in safety net)
  const finalGuaranteedPartnershipUnits = guaranteedPartnershipUnits.filter(unit => {
    const unitPlayers = getUnitPlayers(unit);
    return unitPlayers.every(id => {
      return safetyNetFilteredUnits.some(u => unitContainsPlayer(u, id));
    });
  });

  // Rebuild guaranteed scheduled units
  const finalGuaranteedScheduledUnits = guaranteedScheduledUnits.filter(unit => {
    const unitPlayers = getUnitPlayers(unit);
    return unitPlayers.every(id => {
      return safetyNetFilteredUnits.some(u => unitContainsPlayer(u, id));
    });
  });

  // Identify units guaranteed due to high courtsObserved count (threshold = session's number of courts when provided)
  // If a partnership unit, both players must have high courtsObserved
  const guaranteedByWaitUnits = safetyNetFilteredUnits.filter(unit => {
    const unitPlayers = getUnitPlayers(unit);
    // All players in unit must have courtsObserved >= threshold
    const meetsCourtsObserved = unitPlayers.every(playerId => {
      const waitMetrics = playerWaitMetrics[playerId];
      const courtsObserved = waitMetrics?.courtsObserved || 0;
      return courtsObserved >= effectiveWaitGuarantee;
    });
    
    if (!meetsCourtsObserved) {
      return false;
    }
    
    // Exclude from wait guarantee if ANY player has a non-default match preference
    // (preference holders forego the strict wait guarantee while a preference is set)
    const hasActivePreference =
      matchPreferencesEnabled &&
      unitPlayers.some(playerId => {
      const waitMetrics = playerWaitMetrics[playerId];
      const preference = waitMetrics?.matchPreference || 'no_preference';
      return preference !== 'no_preference';
    });
    
    if (hasActivePreference) {
      debugLog('🎯 Suspending wait guarantee for unit with active match preference:', {
        unitId: unit.id,
        players: unitPlayers.map(id => ({
          name: playersById[id]?.name || id,
          preference: playerWaitMetrics[id]?.matchPreference,
          courtsObserved: playerWaitMetrics[id]?.courtsObserved,
        })),
      });
      return false;
    }
    
    return true;
  });

  if (guaranteedByWaitUnits.length > 0) {
    debugLog(`⏰ Wait guarantee: ${guaranteedByWaitUnits.length} unit(s) with ${effectiveWaitGuarantee}+ courts observed:`, 
      guaranteedByWaitUnits.map(unit => ({
        unitId: unit.id,
        players: getUnitPlayers(unit).map(id => ({
          id,
          name: playersById[id]?.name || id,
          courtsObserved: playerWaitMetrics[id]?.courtsObserved || 0,
        })),
      }))
    );
  }

  const guaranteedUnitCount = finalGuaranteedPartnershipUnits.length + finalGuaranteedScheduledUnits.length;
  const guaranteedPlayerCount = getTotalPlayerCount(finalGuaranteedPartnershipUnits) + getTotalPlayerCount(finalGuaranteedScheduledUnits);
  const totalAtMin = getTotalPlayerCount(unitsAtMin) + guaranteedPlayerCount;

  if (totalAtMin >= ppm) {
    // Build eligible pool from units at min + guaranteed units
    const eligiblePoolUnits = [...unitsAtMin];
    
    // Add guaranteed partnership units
    finalGuaranteedPartnershipUnits.forEach(unit => {
      const unitId = unit.id;
      if (!eligiblePoolUnits.find(u => u.id === unitId)) {
        eligiblePoolUnits.push(unit);
      }
    });
    
    // Add scheduled units to the pool (but NOT as guaranteed)
    // They will compete fairly with other combinations
    finalGuaranteedScheduledUnits.forEach(unit => {
      const unitId = unit.id;
      if (!eligiblePoolUnits.find(u => u.id === unitId)) {
        eligiblePoolUnits.push(unit);
      }
    });

    // DEBUG: verify pool units align with session min (unit averages, not per-player counts)
    const unitAvgsInPool = eligiblePoolUnits.map((unit) => getUnitAverageGames(unit, playersById));
    const uniqueUnitAvgsInPool = [...new Set(unitAvgsInPool)].sort((a, b) => a - b);
    if (
      uniqueUnitAvgsInPool.length > 1
      || (uniqueUnitAvgsInPool.length === 1 && Math.abs(uniqueUnitAvgsInPool[0] - sessionMinGames) > 1e-6)
    ) {
      debugLog('[POOL BUILDER] Pool spans multiple unit-average tiers at early return', {
        sessionMinGames,
        uniqueUnitAvgsInPool,
      });
    }

    // ✅ VALIDATION GATE: Check if this pool can actually form valid combinations
    // This prevents committing to a pool that cannot satisfy game constraints
    const poolIsFeasible = canPoolSatisfyConstraints(
      eligiblePoolUnits,
      playersById,
      completePartnerships,
      gameTypeRestriction,
      ppm,
      playerWaitMetrics
    );
    
    if (!poolIsFeasible) {
      const allPlayerIdsInPool = eligiblePoolUnits.flatMap((unit) => getUnitPlayers(unit));
      debugLog('🚫 Validation gate: Min-games pool cannot form valid combinations - forcing expansion');
      debugLog(`   Pool size: ${eligiblePoolUnits.length} units (${allPlayerIdsInPool.length} players)`);
      debugLog(`   Game type restriction: ${gameTypeRestriction}`);
      relaxationDetails.push('Validation gate failed - forcing pool expansion beyond min games');
    } else {
      const sessionDebtExpand = shouldExpandPoolBeyondMinTier({
        unitsAtMin,
        safetyNetFilteredUnits,
        sessionMinGames,
        playersById,
        playerWaitMetrics,
        sessionState,
        playersPerMatch: ppm,
        ppm,
      });

      if (sessionDebtExpand) {
        debugLog('🔄 Session debt: expanding pool beyond min tier (waitlist depth / variety / quartet repeat risk)');
        relaxationDetails.push('Session debt: pool expansion beyond min tier');
      } else {
      // Pool is feasible - proceed with early return
      debugLog('✅ Validation gate: Pool is feasible');

    // Build guaranteed players list (partnerships ONLY)
    let guaranteedUnits = [];
    if (finalGuaranteedPartnershipUnits.length > 0) {
      finalGuaranteedPartnershipUnits.forEach(unit => {
        if (!guaranteedUnits.find(u => u.id === unit.id)) {
          guaranteedUnits.push(unit);
        }
      });
      const guaranteedPlayerCount = getTotalPlayerCount(guaranteedUnits);
      debugLog(`🎯 Early return: ${guaranteedPlayerCount} players guaranteed (from ${guaranteedUnits.length} partnership units)`);
    } else {
      guaranteedUnits = [];
      const playersAtMinCount = getTotalPlayerCount(unitsAtMin);
      debugLog(`🎯 Early return: Single threshold with ${playersAtMinCount} players at min games ${sessionMinGames} - No guarantees needed (any ${ppm} are equally fair)`);
    }

    // Add wait-guaranteed units to guaranteed list
    guaranteedByWaitUnits.forEach(unit => {
      if (!guaranteedUnits.find(u => u.id === unit.id)) {
        guaranteedUnits.push(unit);
      }
    });

    const guaranteedUnitIds = new Set(guaranteedUnits.map(u => u.id));
    return {
      pool: eligiblePoolUnits,
      guaranteedPlayers: guaranteedUnits,
      flexiblePlayers: eligiblePoolUnits.filter(unit => !guaranteedUnitIds.has(unit.id)),
      minGames: sessionMinGames,
      expandedThreshold: sessionMinGames,
      thresholdsUsed: 1,
      cooldownRelaxed,
      playersPassedCooldown,
      relaxationDetails,
      guaranteedByWait: guaranteedByWaitUnits,
    };
      }
    }
  }

  // STEP 7: Handle threshold expansion (when we need to expand beyond min games)
  // Unique unit-average tiers (singles + partnerships) for threshold stepping.
  const uniqueGamesPlayed = new Set();
  safetyNetFilteredUnits.forEach((unit) => {
    if (unit.type === UNIT_TYPES.SCHEDULED) return;
    uniqueGamesPlayed.add(getUnitAverageGames(unit, playersById));
  });

  const sortedUniqueGames = Array.from(uniqueGamesPlayed).sort((a, b) => a - b);
  debugLog(`📊 Unique games played in session: [${sortedUniqueGames.join(', ')}]`);
  const minGamesIndex = sortedUniqueGames.indexOf(sessionMinGames);
  if (minGamesIndex === -1) {
    console.warn(`⚠️ Min games ${sessionMinGames} not found in unique games list, using all units`);
    const guaranteedUnitIds = new Set(guaranteedByWaitUnits.map(u => u.id));
    return {
      pool: safetyNetFilteredUnits,
      guaranteedPlayers: guaranteedByWaitUnits,
      flexiblePlayers: safetyNetFilteredUnits.filter(unit => !guaranteedUnitIds.has(unit.id)),
      minGames: sessionMinGames,
      expandedThreshold: sortedUniqueGames[sortedUniqueGames.length - 1] || sessionMinGames,
      thresholdsUsed: sortedUniqueGames.length,
      cooldownRelaxed,
      playersPassedCooldown,
      relaxationDetails,
      guaranteedByWait: guaranteedByWaitUnits,
    };
  }

  // Expand pool by threshold until we have enough units for interaction flexibility
  // Doubles: prefer 5+ players; singles: 3+ gives at least one alternate when 2 are on court
  const minPlayersNeeded = ppm === 2 ? 3 : 5;
  let eligiblePoolUnits = [];
  let thresholdIndex = minGamesIndex;
  const maxIndex = sortedUniqueGames.length - 1;

  while (getTotalPlayerCount(eligiblePoolUnits) < minPlayersNeeded && thresholdIndex <= maxIndex) {
    const threshold = sortedUniqueGames[thresholdIndex];
    // Filter units where ALL players in unit meet threshold
    eligiblePoolUnits = safetyNetFilteredUnits.filter((unit) => {
      if (unit.type === UNIT_TYPES.SCHEDULED) return false;
      return getUnitAverageGames(unit, playersById) <= threshold;
    });
    
    const playerCount = getTotalPlayerCount(eligiblePoolUnits);
    if (playerCount < minPlayersNeeded && thresholdIndex < maxIndex) {
      const nextThreshold = sortedUniqueGames[thresholdIndex + 1];
      debugLog(`🔄 Pool has ${playerCount} players (${eligiblePoolUnits.length} units) at threshold ${threshold}, expanding to ${nextThreshold}`);
      thresholdIndex++;
    } else {
      break;
    }
  }

  const finalThreshold = sortedUniqueGames[thresholdIndex];
  const numberOfThresholdsUsed = thresholdIndex - minGamesIndex + 1;
  const finalPlayerCount = getTotalPlayerCount(eligiblePoolUnits);
  
  debugLog(`✅ Pool expansion complete: ${finalPlayerCount} players, threshold ${finalThreshold} (used ${numberOfThresholdsUsed} threshold(s))`);
  
  if (numberOfThresholdsUsed > 1) {
    relaxationDetails.push(`Expanded pool to ${finalThreshold} games (${numberOfThresholdsUsed} thresholds)`);
  }

  // Build guaranteed units list
  let guaranteedUnits = [];
  if (numberOfThresholdsUsed >= 3) {
    // Guarantee all units below final threshold
    guaranteedUnits = eligiblePoolUnits.filter(unit => {
      const unitGames = getUnitEffectiveGames(unit, playersById);
      return unitGames < finalThreshold;
    });
  } else if (numberOfThresholdsUsed === 2) {
    guaranteedUnits = unitsAtMin.slice();
  } else if (getTotalPlayerCount(unitsAtMin) < ppm) {
    guaranteedUnits = unitsAtMin.slice();
  }

  // Add guaranteed partnership and scheduled units
  finalGuaranteedPartnershipUnits.forEach(unit => {
    if (!guaranteedUnits.find(u => u.id === unit.id)) {
      guaranteedUnits.push(unit);
    }
  });

  finalGuaranteedScheduledUnits.forEach(unit => {
    if (!guaranteedUnits.find(u => u.id === unit.id)) {
      guaranteedUnits.push(unit);
    }
  });

  // Add wait-guaranteed units to guaranteed list
  guaranteedByWaitUnits.forEach(unit => {
    if (!guaranteedUnits.find(u => u.id === unit.id)) {
      guaranteedUnits.push(unit);
    }
  });

  const guaranteedUnitIds = new Set(guaranteedUnits.map(u => u.id));
  const flexibleUnits = eligiblePoolUnits.filter(unit => !guaranteedUnitIds.has(unit.id));

  return {
    pool: eligiblePoolUnits,
    guaranteedPlayers: guaranteedUnits,
    flexiblePlayers: flexibleUnits,
    minGames: sessionMinGames,
    expandedThreshold: finalThreshold,
    thresholdsUsed: numberOfThresholdsUsed,
    cooldownRelaxed,
    playersPassedCooldown,
    relaxationDetails,
    guaranteedByWait: guaranteedByWaitUnits,
  };
}

export {
  buildEligiblePool,
  calculateMinGamesExcludingPartnerships,
  calculateSessionMinGames,
  getUnitAverageGames,
  canPartnershipPlay,
  canScheduledGamePlay,
  isInPartnership,
  isInScheduledGame,
  isPartnershipEligibleForGuarantee,
  isScheduledGameEligibleForGuarantee,
  wouldBeFairAfterGame,
  wouldReachMinPlus2,
};

